//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  IInstagramMentionedAndTaggedAutocompleteRequest,
  IInstagramMentionedAndTaggedMediaResponse,
  IInstagramMentionedMediaRequest,
  IInstagramMentionedMediaRequests,
} from '@apis/instagram-mentioned-and-tagged/InstagramMentionedAndTaggedApiModel';
import { deserializeRightsPermissions } from '@apis/rights-management/rightsManagementApiUtils';
import { IInstagramAutocompleteResponse } from '@instagram-account/models/IInstagramAutocompleteResponses';
import {
  IInstagramMultiSearchRequestBody,
  IInstagramSearchRequestBody,
} from '@instagram-account/models/IInstagramSearchRequestBody';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { Serialized } from '@utils/types/Serialized';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const deserializeMediaItem = (
  item: Serialized<IMlsMediaItem>,
): IMlsMediaItem => ({
  ...item,
  rightsPermissions: deserializeRightsPermissions(item.rightsPermissions),
  networkItem: item.networkItem && {
    ...item.networkItem,
    observationTime: new Date(item.networkItem.observationTime),
    postedTime: new Date(item.networkItem.postedTime),
  },
});

function mentionedAndTaggedFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/mentions/${endpoint}`,
    init,
  );
}

function mentionedAndTaggedMultiHandleFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v2/mentions/${endpoint}`,
    init,
  );
}

/**
 *
 * Gets the mentioned and tagged instgram posts for the given IG username within a given timeframe
 *
 * /api/v1/mentions/media
 *
 * @returns A set of data which includes a list of instagram post
 */
export async function getMentionedAndTaggedMedia(
  req: IInstagramMentionedMediaRequest,
  body?: IInstagramSearchRequestBody,
  init?: RequestInit,
): Promise<IInstagramMentionedAndTaggedMediaResponse> {
  const params = makeParams(req);
  const res = await mentionedAndTaggedFetch(`media?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
    ...init,
  });
  return res.json<IInstagramMentionedAndTaggedMediaResponse>((res) => ({
    data: {
      ...res.data,
      media: res.data.media.map(deserializeMediaItem),
    },
  }));
}

/**
 *
 * Get autocomplete suggestions for mentioned & tagged
 *
 * /api/v1/mentions/autocomplete
 *
 * @returns A list of matching autocomplete results
 */
export async function getMentionedAndTaggedAutocompleteSuggestions(
  req: IInstagramMentionedAndTaggedAutocompleteRequest,
  body?: IInstagramSearchRequestBody,
  init?: RequestInit,
): Promise<IInstagramAutocompleteResponse> {
  const params = makeParams(req);
  const res = await mentionedAndTaggedFetch(`autocomplete?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
    ...init,
  });
  return res.json();
}

/**
 *
 * Downloads a zip file containing a summary and content for mentioned & tagged
 *
 * /api/v1/mentions/export.zip
 */
export async function getMentionedAndTaggedContentZip(
  req: IInstagramMentionedMediaRequest,
  body?: IInstagramSearchRequestBody,
  init?: RequestInit,
): Promise<Blob> {
  const params = makeParams(req);
  const res = await mentionedAndTaggedFetch(`export.zip?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
    ...init,
  });
  return res.blob();
}

/** Multi Handle Mentioned and Tagged
 *
 * Gets the mentioned and tagged instgram posts for the given IG username within a given timeframe
 *
 * /api/v2/mentions/media
 *
 * @returns A set of data which includes a list of instagram post
 */
export async function getMultiHandleMentionedAndTaggedMedia(
  req: IInstagramMentionedMediaRequests,
  body?: IInstagramMultiSearchRequestBody,
  init?: RequestInit,
): Promise<IInstagramMentionedAndTaggedMediaResponse> {
  const params = makeParams(req);
  const res = await mentionedAndTaggedMultiHandleFetch(`media?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
    ...init,
  });
  return res.json<IInstagramMentionedAndTaggedMediaResponse>((res) => ({
    data: {
      ...res.data,
      media: res.data.media.map(deserializeMediaItem),
    },
  }));
}
